import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { DatePicker, Select, Table, Card, Row, Col, Typography, Space, message, Segmented, Divider } from "antd";
import dayjs from "dayjs";
import { local_backend } from "../../config";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const DEFAULT_DAYS = 90;

const numberFormat = (val) =>
  new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val || 0);
const formatAED = (val) => `AED ${numberFormat(val)}`;

const joinUrl = (base, path) => {
  const b = String(base || "").replace(/\/+$/, "");
  const p = String(path || "").replace(/^\/+/, "");
  return `${b}/${p}`;
};

const pickAmount = (row) =>
  Number(
    row?.totalTransactionAmount ??
    row?.transactionAmount ??
    row?.totalChargedAmountAED ??
    row?.chargedAmountAED ??
    0
  ) || 0;

const pickOutOfPolicyAmount = (row) =>
  Number(row?.totalOutOfPolicyAmount ?? row?.outOfPolicyAmount ?? 0) || 0;

const pickDate = (row) =>
  row?.lastTransactionDateTime ||
  row?.transactionDateTime ||
  row?.transactionDate ||
  row?.createdOn;

const pickCategory = (row) => {
  const c = (row?.category ?? "").toString().trim();
  return c || "Uncategorized";
};

// Palette for the category donut slices (cycled if there are more slices).
const DONUT_COLORS = [
  "#722ed1", "#1677ff", "#52c41a", "#fa8c16", "#eb2f96",
  "#13c2c2", "#faad14", "#f5222d", "#2f54eb", "#a0d911",
];

// Dependency-free donut chart: renders category share of total as an SVG ring
// with a legend. `data` is [{ label, value }], already sorted desc.
const CategoryDonut = ({ data, total }) => {
  if (!data?.length || !total) {
    return <Text type="secondary">No data available</Text>;
  }

  const size = 200;
  const stroke = 34;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const center = size / 2;

  let cumulative = 0;
  const arcs = data.map((slice, i) => {
    const fraction = slice.value / total;
    const dash = fraction * c;
    const arc = {
      key: slice.label,
      color: DONUT_COLORS[i % DONUT_COLORS.length],
      dashArray: `${dash} ${c - dash}`,
      dashOffset: -cumulative * c,
      percent: fraction * 100,
    };
    cumulative += fraction;
    return arc;
  });

  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 24 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Category spend share">
        <g transform={`rotate(-90 ${center} ${center})`}>
          <circle cx={center} cy={center} r={r} fill="none" stroke="#f0f2f5" strokeWidth={stroke} />
          {arcs.map((a) => (
            <circle
              key={a.key}
              cx={center}
              cy={center}
              r={r}
              fill="none"
              stroke={a.color}
              strokeWidth={stroke}
              strokeDasharray={a.dashArray}
              strokeDashoffset={a.dashOffset}
            />
          ))}
        </g>
        <text x={center} y={center - 4} textAnchor="middle" fontSize="12" fill="#8c8c8c">Total</text>
        <text x={center} y={center + 16} textAnchor="middle" fontSize="15" fontWeight="600" fill="#262626">
          {formatAED(total)}
        </text>
      </svg>

      <div style={{ flex: 1, minWidth: 220 }}>
        {data.map((slice, i) => (
          <div
            key={slice.label}
            style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}
          >
            <span
              style={{
                width: 12,
                height: 12,
                borderRadius: 3,
                background: DONUT_COLORS[i % DONUT_COLORS.length],
                flex: "0 0 auto",
              }}
            />
            <Text ellipsis style={{ flex: 1 }}>{slice.label}</Text>
            <Text type="secondary">{numberFormat((slice.value / total) * 100)}%</Text>
            <Text strong style={{ minWidth: 110, textAlign: "right" }}>{formatAED(slice.value)}</Text>
          </div>
        ))}
      </div>
    </div>
  );
};

const ExpensesDashboard = () => {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dateRange, setDateRange] = useState([
    dayjs().subtract(DEFAULT_DAYS, "day"),
    dayjs()
  ]);
  const [selectedCards, setSelectedCards] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [timeGroup, setTimeGroup] = useState("day"); // day | month
  const [viewMode, setViewMode] = useState("data"); // data | chart

  // Endpoint can be overridden via env var REACT_APP_EXPENSES_ENDPOINT_PATH
  const endpointPath = process.env.REACT_APP_EXPENSES_ENDPOINT_PATH || "OpenAPI/ExpenseTransactions";
  const endpointUrl = joinUrl(local_backend, endpointPath);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    setError(null);
    axios
      .get(endpointUrl)
      .then((res) => {
        if (!isMounted) return;
        const items =
          (Array.isArray(res.data) && res.data) ||
          res.data?.items ||
          res.data?.data ||
          res.data?.result ||
          [];
        setRawData(items);
      })
      .catch((err) => {
        console.error("Failed to load expenses:", err);
        const serverMessage =
          err?.response?.data?.message ||
          err?.response?.data?.title ||
          (typeof err?.response?.data === "string" ? err.response.data : null) ||
          err?.message ||
          "Unknown error";
        const finalMessage = `Unable to load expense transactions (${endpointUrl}) - ${serverMessage}`;
        setError(finalMessage);
        message.error(finalMessage);
      })
      .finally(() => isMounted && setLoading(false));

    return () => {
      isMounted = false;
    };
  }, [endpointUrl]);

  // Distinct filters
  const cardOptions = useMemo(() => {
    const set = new Set();
    rawData.forEach((r) => r?.cardName && set.add(r.cardName));
    return Array.from(set).sort().map((c) => ({ label: c, value: c }));
  }, [rawData]);

  const employeeOptions = useMemo(() => {
    const set = new Set();
    rawData.forEach((r) => r?.employeeFullName && set.add(r.employeeFullName));
    return Array.from(set).sort().map((e) => ({ label: e, value: e }));
  }, [rawData]);

  const categoryOptions = useMemo(() => {
    const set = new Set();
    rawData.forEach((r) => set.add(pickCategory(r)));
    return Array.from(set).sort().map((c) => ({ label: c, value: c }));
  }, [rawData]);

  // Filtering
  const filtered = useMemo(() => {
    const [start, end] = dateRange || [];
    return rawData.filter((r) => {
      // date filter
      const d = pickDate(r);
      const m = d ? dayjs(d) : null;
      const inDate = start && end ? m && (m.isSame(start, "day") || m.isSame(end, "day") || (m.isAfter(start, "day") && m.isBefore(end, "day"))) : true;

      // card filter
      const inCard = selectedCards.length ? selectedCards.includes(r.cardName) : true;

      // employee filter
      const inEmp = selectedEmployees.length ? selectedEmployees.includes(r.employeeFullName) : true;

      // category filter
      const inCat = selectedCategories.length ? selectedCategories.includes(pickCategory(r)) : true;

      return inDate && inCard && inEmp && inCat;
    });
  }, [rawData, dateRange, selectedCards, selectedEmployees, selectedCategories]);

  // KPIs
  const kpis = useMemo(() => {
    const total = filtered.reduce((sum, r) => sum + pickAmount(r), 0);
    const outOfPolicy = filtered.reduce((sum, r) => sum + pickOutOfPolicyAmount(r), 0);
    const transactions = filtered.length;
    const uniqueEmployees = new Set(filtered.map((r) => r.employeeFullName)).size;
    const uniqueCards = new Set(filtered.map((r) => r.cardName)).size;
    return { total, outOfPolicy, transactions, uniqueEmployees, uniqueCards };
  }, [filtered]);

  // Aggregations
  const byCard = useMemo(() => {
    const map = new Map();
    filtered.forEach((r) => {
      const key = r.cardName || "Unknown";
      const current = map.get(key) || { cardName: key, Transactions: 0, TotalAmount: 0, Employees: new Set() };
      current.Transactions += 1;
      current.TotalAmount += pickAmount(r);
      if (r.employeeFullName) current.Employees.add(r.employeeFullName);
      map.set(key, current);
    });
    return Array.from(map.values()).map((x) => ({ ...x, EmployeesCount: x.Employees.size }));
  }, [filtered]);

  const byEmployee = useMemo(() => {
    const map = new Map();
    filtered.forEach((r) => {
      const key = r.employeeFullName || "Unknown";
      const current = map.get(key) || { employeeFullName: key, Transactions: 0, TotalAmount: 0, Cards: new Set() };
      current.Transactions += 1;
      current.TotalAmount += pickAmount(r);
      if (r.cardName) current.Cards.add(r.cardName);
      map.set(key, current);
    });
    return Array.from(map.values()).map((x) => ({ ...x, CardsCount: x.Cards.size }));
  }, [filtered]);

  const byCategory = useMemo(() => {
    const map = new Map();
    filtered.forEach((r) => {
      const key = pickCategory(r);
      const current = map.get(key) || { category: key, Transactions: 0, TotalAmount: 0, Employees: new Set() };
      current.Transactions += 1;
      current.TotalAmount += pickAmount(r);
      if (r.employeeFullName) current.Employees.add(r.employeeFullName);
      map.set(key, current);
    });
    return Array.from(map.values())
      .map((x) => ({ ...x, EmployeesCount: x.Employees.size }))
      .sort((a, b) => b.TotalAmount - a.TotalAmount);
  }, [filtered]);

  // For each employee, the category they spend the most in.
  const employeeTopCategory = useMemo(() => {
    const map = new Map();
    filtered.forEach((r) => {
      const emp = r.employeeFullName || "Unknown";
      const cat = pickCategory(r);
      const amt = pickAmount(r);
      const current = map.get(emp) || { employeeFullName: emp, TotalAmount: 0, categories: new Map() };
      current.TotalAmount += amt;
      current.categories.set(cat, (current.categories.get(cat) || 0) + amt);
      map.set(emp, current);
    });
    return Array.from(map.values())
      .map((x) => {
        let TopCategory = "-";
        let TopCategoryAmount = 0;
        x.categories.forEach((amt, cat) => {
          if (amt > TopCategoryAmount) {
            TopCategoryAmount = amt;
            TopCategory = cat;
          }
        });
        const Share = x.TotalAmount ? (TopCategoryAmount / x.TotalAmount) * 100 : 0;
        return {
          employeeFullName: x.employeeFullName,
          TopCategory,
          TopCategoryAmount,
          TotalAmount: x.TotalAmount,
          Share,
        };
      })
      .sort((a, b) => b.TopCategoryAmount - a.TopCategoryAmount);
  }, [filtered]);

  const byTime = useMemo(() => {
    const map = new Map();
    filtered.forEach((r) => {
      const d = pickDate(r);
      const m = d ? dayjs(d) : null;
      if (!m) return;
      const key = timeGroup === "month" ? m.format("YYYY-MM") : m.format("YYYY-MM-DD");
      const current = map.get(key) || { Period: key, Transactions: 0, TotalAmount: 0 };
      current.Transactions += 1;
      current.TotalAmount += pickAmount(r);
      map.set(key, current);
    });
    // sort by date ascending
    return Array.from(map.values()).sort((a, b) => (a.Period > b.Period ? 1 : -1));
  }, [filtered, timeGroup]);

  const chartByCard = useMemo(
    () =>
      [...byCard]
        .sort((a, b) => b.TotalAmount - a.TotalAmount)
        .slice(0, 8),
    [byCard]
  );

  const chartByEmployee = useMemo(
    () =>
      [...byEmployee]
        .sort((a, b) => b.TotalAmount - a.TotalAmount)
        .slice(0, 8),
    [byEmployee]
  );

  const chartByTime = useMemo(
    () =>
      [...byTime]
        .sort((a, b) => b.TotalAmount - a.TotalAmount)
        .slice(0, 12),
    [byTime]
  );

  // Donut data: keep the top categories as individual slices and fold the rest
  // into a single "Others" slice so the ring stays readable. Share is computed
  // against the full total so percentages still add up to 100%.
  const categoryDonut = useMemo(() => {
    const total = byCategory.reduce((sum, x) => sum + x.TotalAmount, 0);
    const TOP_N = 7;
    const top = byCategory.slice(0, TOP_N);
    const rest = byCategory.slice(TOP_N);
    const restTotal = rest.reduce((sum, x) => sum + x.TotalAmount, 0);
    const slices = top.map((x) => ({ label: x.category, value: x.TotalAmount }));
    if (restTotal > 0) {
      slices.push({ label: `Others (${rest.length})`, value: restTotal });
    }
    return { total, slices };
  }, [byCategory]);

  const chartEmployeeTopCategory = useMemo(
    () => [...employeeTopCategory].slice(0, 8),
    [employeeTopCategory]
  );

  const getBarWidth = (value, maxValue) => {
    if (!maxValue) return "0%";
    return `${Math.max(4, Math.round((value / maxValue) * 100))}%`;
  };

  const cardColumns = [
    { title: "Card", dataIndex: "cardName", key: "cardName" },
    { title: "Transactions", dataIndex: "Transactions", key: "Transactions", width: 140 },
    {
      title: "Total Amount",
      dataIndex: "TotalAmount",
      key: "TotalAmount",
      width: 160,
      render: (v) => formatAED(v),
    },
    { title: "Employees", dataIndex: "EmployeesCount", key: "EmployeesCount", width: 140 },
  ];

  const employeeColumns = [
    { title: "Employee", dataIndex: "employeeFullName", key: "employeeFullName" },
    { title: "Transactions", dataIndex: "Transactions", key: "Transactions", width: 140 },
    {
      title: "Total Amount",
      dataIndex: "TotalAmount",
      key: "TotalAmount",
      width: 160,
      render: (v) => formatAED(v),
    },
    { title: "Cards", dataIndex: "CardsCount", key: "CardsCount", width: 120 },
  ];

  const categoryColumns = [
    { title: "Category", dataIndex: "category", key: "category" },
    { title: "Transactions", dataIndex: "Transactions", key: "Transactions", width: 140 },
    {
      title: "Total Amount",
      dataIndex: "TotalAmount",
      key: "TotalAmount",
      width: 160,
      render: (v) => formatAED(v),
    },
    { title: "Employees", dataIndex: "EmployeesCount", key: "EmployeesCount", width: 130 },
  ];

  const employeeTopCategoryColumns = [
    { title: "Employee", dataIndex: "employeeFullName", key: "employeeFullName" },
    { title: "Top Category", dataIndex: "TopCategory", key: "TopCategory" },
    {
      title: "Spent in Category",
      dataIndex: "TopCategoryAmount",
      key: "TopCategoryAmount",
      width: 160,
      render: (v) => formatAED(v),
    },
    {
      title: "% of Their Spend",
      dataIndex: "Share",
      key: "Share",
      width: 150,
      render: (v) => `${numberFormat(v)}%`,
    },
  ];

  const timeColumns = [
    { title: timeGroup === "month" ? "Month" : "Date", dataIndex: "Period", key: "Period", width: 140 },
    { title: "Transactions", dataIndex: "Transactions", key: "Transactions", width: 140 },
    {
      title: "Total Amount",
      dataIndex: "TotalAmount",
      key: "TotalAmount",
      width: 160,
      render: (v) => formatAED(v),
    },
  ];

  return (
    <div className="px-4 sm:px-8 py-4">
      <Title level={3} style={{ marginBottom: 16 }}>Expenses Dashboard</Title>
      {error ? <Text type="danger">{error}</Text> : null}

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={24} md={10} lg={8}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text strong>Date Range</Text>
              <RangePicker
                allowEmpty={[false, false]}
                value={dateRange}
                onChange={(v) => setDateRange(v)}
                style={{ width: "100%" }}
              />
            </Space>
          </Col>
          <Col xs={24} sm={12} md={7} lg={6}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text strong>Card Name</Text>
              <Select
                mode="multiple"
                allowClear
                placeholder="All Cards"
                options={cardOptions}
                value={selectedCards}
                onChange={setSelectedCards}
                maxTagCount="responsive"
                style={{width: 400}}
              />
            </Space>
          </Col>
          <Col xs={24} sm={12} md={7} lg={6}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text strong>Employee</Text>
              <Select
                mode="multiple"
                allowClear
                placeholder="All Employees"
                options={employeeOptions}
                value={selectedEmployees}
                onChange={setSelectedEmployees}
                maxTagCount="responsive"
                style={{width: 400}}
              />
            </Space>
          </Col>
          <Col xs={24} sm={12} md={7} lg={6}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text strong>Category</Text>
              <Select
                mode="multiple"
                allowClear
                placeholder="All Categories"
                options={categoryOptions}
                value={selectedCategories}
                onChange={setSelectedCategories}
                maxTagCount="responsive"
                style={{ width: 400 }}
              />
            </Space>
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text strong>Time Group</Text>
              <Segmented
                options={[{ label: "Day", value: "day" }, { label: "Month", value: "month" }]}
                value={timeGroup}
                onChange={setTimeGroup}
              />
            </Space>
          </Col>
          <Col xs={24} sm={12} md={6} lg={4}>
            <Space direction="vertical" style={{ width: "100%" }}>
              <Text strong>View</Text>
              <Segmented
                options={[{ label: "Data View", value: "data" }, { label: "Chart View", value: "chart" }]}
                value={viewMode}
                onChange={setViewMode}
              />
            </Space>
          </Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Text type="secondary">Total Spend</Text>
            <Title level={3} style={{ marginTop: 8 }}>{formatAED(kpis.total)}</Title>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Text type="secondary">Transactions</Text>
            <Title level={3} style={{ marginTop: 8 }}>{kpis.transactions}</Title>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Text type="secondary">Employees</Text>
            <Title level={3} style={{ marginTop: 8 }}>{kpis.uniqueEmployees}</Title>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Text type="secondary">Cards</Text>
            <Title level={3} style={{ marginTop: 8 }}>{kpis.uniqueCards}</Title>
          </Card>
        </Col>
      </Row>

      {viewMode === "data" ? (
        <>
          <Divider />

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="By Card" loading={loading}>
                <Table
                  rowKey={(r) => r.cardName}
                  dataSource={byCard}
                  columns={cardColumns}
                  size="small"
                  pagination={{ pageSize: 8 }}
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="By Employee" loading={loading}>
                <Table
                  rowKey={(r) => r.employeeFullName}
                  dataSource={byEmployee}
                  columns={employeeColumns}
                  size="small"
                  pagination={{ pageSize: 8 }}
                />
              </Card>
            </Col>
          </Row>

          <Divider />

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="By Category" loading={loading}>
                <Table
                  rowKey={(r) => r.category}
                  dataSource={byCategory}
                  columns={categoryColumns}
                  size="small"
                  pagination={{ pageSize: 8 }}
                />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Employee — Top Spending Category" loading={loading}>
                <Table
                  rowKey={(r) => r.employeeFullName}
                  dataSource={employeeTopCategory}
                  columns={employeeTopCategoryColumns}
                  size="small"
                  pagination={{ pageSize: 8 }}
                />
              </Card>
            </Col>
          </Row>

          <Divider />

          <Card title={timeGroup === "month" ? "Spend by Month" : "Spend by Day"} loading={loading}>
            <Table
              rowKey={(r) => r.Period}
              dataSource={byTime}
              columns={timeColumns}
              size="small"
              pagination={{ pageSize: 12 }}
            />
          </Card>
        </>
      ) : (
        <>
          <Divider />
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="Chart: Top Cards by Spend" loading={loading}>
                {chartByCard.length ? chartByCard.map((item) => {
                  const max = chartByCard[0]?.TotalAmount || 0;
                  return (
                    <div key={item.cardName} style={{ marginBottom: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                        <Text ellipsis>{item.cardName}</Text>
                        <Text strong>{formatAED(item.TotalAmount)}</Text>
                      </div>
                      <div style={{ marginTop: 6, background: "#f0f2f5", borderRadius: 8, height: 10 }}>
                        <div
                          style={{
                            width: getBarWidth(item.TotalAmount, max),
                            height: 10,
                            borderRadius: 8,
                            background: "#1677ff",
                          }}
                        />
                      </div>
                    </div>
                  );
                }) : <Text type="secondary">No data available</Text>}
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Chart: Top Employees by Spend" loading={loading}>
                {chartByEmployee.length ? chartByEmployee.map((item) => {
                  const max = chartByEmployee[0]?.TotalAmount || 0;
                  return (
                    <div key={item.employeeFullName} style={{ marginBottom: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                        <Text ellipsis>{item.employeeFullName}</Text>
                        <Text strong>{formatAED(item.TotalAmount)}</Text>
                      </div>
                      <div style={{ marginTop: 6, background: "#f0f2f5", borderRadius: 8, height: 10 }}>
                        <div
                          style={{
                            width: getBarWidth(item.TotalAmount, max),
                            height: 10,
                            borderRadius: 8,
                            background: "#52c41a",
                          }}
                        />
                      </div>
                    </div>
                  );
                }) : <Text type="secondary">No data available</Text>}
              </Card>
            </Col>
          </Row>

          <Divider />

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title="Chart: Category Spend Share" loading={loading}>
                <CategoryDonut data={categoryDonut.slices} total={categoryDonut.total} />
              </Card>
            </Col>
            <Col xs={24} lg={12}>
              <Card title="Chart: Employees by Top-Category Spend" loading={loading}>
                {chartEmployeeTopCategory.length ? chartEmployeeTopCategory.map((item) => {
                  const max = chartEmployeeTopCategory[0]?.TopCategoryAmount || 0;
                  return (
                    <div key={item.employeeFullName} style={{ marginBottom: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                        <Text ellipsis>{`${item.employeeFullName} — ${item.TopCategory}`}</Text>
                        <Text strong>{formatAED(item.TopCategoryAmount)}</Text>
                      </div>
                      <div style={{ marginTop: 6, background: "#f0f2f5", borderRadius: 8, height: 10 }}>
                        <div
                          style={{
                            width: getBarWidth(item.TopCategoryAmount, max),
                            height: 10,
                            borderRadius: 8,
                            background: "#eb2f96",
                          }}
                        />
                      </div>
                    </div>
                  );
                }) : <Text type="secondary">No data available</Text>}
              </Card>
            </Col>
          </Row>

          <Divider />

          <Card title={`Chart: ${timeGroup === "month" ? "Spend by Month" : "Spend by Day"}`} loading={loading}>
            {chartByTime.length ? chartByTime.map((item) => {
              const max = chartByTime[0]?.TotalAmount || 0;
              return (
                <div key={item.Period} style={{ marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    <Text>{item.Period}</Text>
                    <Text strong>{formatAED(item.TotalAmount)}</Text>
                  </div>
                  <div style={{ marginTop: 6, background: "#f0f2f5", borderRadius: 8, height: 10 }}>
                    <div
                      style={{
                        width: getBarWidth(item.TotalAmount, max),
                        height: 10,
                        borderRadius: 8,
                        background: "#fa8c16",
                      }}
                    />
                  </div>
                </div>
              );
            }) : <Text type="secondary">No data available</Text>}
          </Card>
        </>
      )}
    </div>
  );
};

export default ExpensesDashboard;
